/**
 * Session Timeout Service
 * Handles automatic sign-out after 1 hour of inactivity
 */

const SESSION_TIMEOUT_KEY = 'lucidDock_lastActivity';
const TIMEOUT_DURATION_MS = 60 * 60 * 1000; // 1 hour in milliseconds

class SessionTimeoutService {
  private activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
  private throttleTimeout: number | null = null;
  private checkInterval: number | null = null;

  /**
   * Update the last activity timestamp
   */
  updateActivity(): void {
    localStorage.setItem(SESSION_TIMEOUT_KEY, Date.now().toString());
  }

  /**
   * Get the last activity timestamp
   */
  getLastActivity(): number {
    const timestamp = localStorage.getItem(SESSION_TIMEOUT_KEY);
    return timestamp ? parseInt(timestamp, 10) : Date.now();
  }

  /**
   * Check if the session has expired
   */
  isSessionExpired(): boolean {
    const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
    if (!isAuthenticated) {
      return false; // Not logged in, no session to expire
    }

    const lastActivity = this.getLastActivity();
    const now = Date.now();
    const elapsed = now - lastActivity;

    return elapsed > TIMEOUT_DURATION_MS;
  }

  /**
   * Get remaining time until session expires (in milliseconds)
   */
  getRemainingTime(): number {
    const lastActivity = this.getLastActivity();
    const now = Date.now();
    const elapsed = now - lastActivity;
    const remaining = TIMEOUT_DURATION_MS - elapsed;
    return Math.max(0, remaining);
  }

  /**
   * Clear session data and redirect to login
   */
  async expireSession(): Promise<void> {
    // Clear all auth-related localStorage items
    localStorage.removeItem('lucidDock_sessionToken');
    localStorage.removeItem('lucidDock_userId');
    localStorage.removeItem('lucidDock_userType');
    localStorage.removeItem('lucidDock_familyDriveRootFolderId');
    localStorage.removeItem('lucidDock_familyDriveOwnerEmail');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem(SESSION_TIMEOUT_KEY);
    localStorage.removeItem('profilePictureUrl');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('firstName');
    localStorage.removeItem('lastName');

    // Clear sidebar state so fresh login gets default behavior
    localStorage.removeItem('sidebarCollapsed');

    // Clear pending bundle import (so different users don't see each other's pending imports)
    localStorage.removeItem('pendingBundleImport');
  }

  /**
   * Start listening for user activity
   * Uses throttling to avoid excessive localStorage writes
   */
  startActivityTracking(): () => void {
    // Set initial activity timestamp
    this.updateActivity();

    // Throttled activity handler - updates at most once per 30 seconds
    const handleActivity = () => {
      if (this.throttleTimeout === null) {
        this.updateActivity();
        this.throttleTimeout = window.setTimeout(() => {
          this.throttleTimeout = null;
        }, 30000); // 30 second throttle
      }
    };

    // Add event listeners
    this.activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Return cleanup function
    return () => {
      this.activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      if (this.throttleTimeout !== null) {
        clearTimeout(this.throttleTimeout);
        this.throttleTimeout = null;
      }
    };
  }

  /**
   * Start periodic session check
   * Checks every minute if session has expired
   */
  startSessionCheck(onExpired: () => void): () => void {
    // Check immediately on start
    if (this.isSessionExpired()) {
      onExpired();
      return () => {};
    }

    // Check every minute
    this.checkInterval = window.setInterval(() => {
      if (this.isSessionExpired()) {
        onExpired();
      }
    }, 60000); // Check every 60 seconds

    // Return cleanup function
    return () => {
      if (this.checkInterval !== null) {
        clearInterval(this.checkInterval);
        this.checkInterval = null;
      }
    };
  }

  /**
   * Clear the last activity timestamp (called on logout)
   */
  clearActivity(): void {
    localStorage.removeItem(SESSION_TIMEOUT_KEY);
  }
}

export const sessionTimeoutService = new SessionTimeoutService();
